import type { Chapter } from '../types.ts';

export function renderMobileNav(chapters: Chapter[]): HTMLElement {
  const root = document.createElement('div');
  root.className = 'mobile-nav';

  const badge = document.createElement('button');
  badge.type = 'button';
  badge.className = 'mobile-nav-badge';
  badge.setAttribute('aria-label', 'Open chapter list');
  badge.setAttribute('aria-expanded', 'false');
  badge.innerHTML = `
    <span class="mobile-nav-current">Ch 1</span>
    <span class="mobile-nav-divider">·</span>
    <span class="mobile-nav-total">${chapters.length}</span>
  `;

  const sheet = document.createElement('aside');
  sheet.className = 'mobile-nav-sheet';
  sheet.setAttribute('aria-label', 'Chapter list');
  sheet.setAttribute('aria-hidden', 'true');
  sheet.innerHTML = `
    <header class="mobile-nav-sheet-head">
      <span>Chapters</span>
      <button type="button" class="mobile-nav-close" aria-label="Close">×</button>
    </header>
    <ol class="mobile-nav-list">
      ${chapters
        .map(
          (c) => `
        <li>
          <a href="#${c.number}-${c.slug}" data-chapter="${c.number}">
            <span class="mobile-nav-num">${String(c.number).padStart(2, '0')}</span>
            <span class="mobile-nav-title">${c.title}</span>
          </a>
        </li>
      `,
        )
        .join('')}
    </ol>
  `;

  root.append(badge, sheet);
  return root;
}

export function setupMobileNav(root: HTMLElement, chapters: Chapter[]): void {
  const badge = root.querySelector<HTMLButtonElement>('.mobile-nav-badge');
  const sheet = root.querySelector<HTMLElement>('.mobile-nav-sheet');
  const closeBtn = root.querySelector<HTMLButtonElement>('.mobile-nav-close');
  const current = root.querySelector<HTMLElement>('.mobile-nav-current');
  const links = root.querySelectorAll<HTMLAnchorElement>('.mobile-nav-list a');
  if (!badge || !sheet || !closeBtn || !current) return;

  function open(): void {
    sheet!.classList.add('is-open');
    sheet!.setAttribute('aria-hidden', 'false');
    badge!.setAttribute('aria-expanded', 'true');
    document.body.style.overflow = 'hidden';
  }

  function close(): void {
    sheet!.classList.remove('is-open');
    sheet!.setAttribute('aria-hidden', 'true');
    badge!.setAttribute('aria-expanded', 'false');
    document.body.style.overflow = '';
  }

  badge.addEventListener('click', () => {
    if (sheet.classList.contains('is-open')) close();
    else open();
  });
  closeBtn.addEventListener('click', close);
  sheet.addEventListener('click', (e) => {
    if (e.target === sheet) close();
  });
  links.forEach((link) => link.addEventListener('click', () => close()));
  window.addEventListener('keydown', (e) => {
    if (e.key === 'Escape' && sheet.classList.contains('is-open')) close();
  });

  function setActive(n: number): void {
    if (current) current.textContent = `Ch ${n}`;
    links.forEach((link) => {
      const isActive = Number(link.dataset['chapter']) === n;
      link.classList.toggle('is-active', isActive);
    });
  }

  const observer = new IntersectionObserver(
    (entries) => {
      const visible = entries
        .filter((e) => e.isIntersecting)
        .sort((a, b) => b.intersectionRatio - a.intersectionRatio);
      const top = visible[0];
      if (!top) return;
      const n = Number((top.target as HTMLElement).dataset['chapter']);
      if (!Number.isNaN(n) && n >= 1 && n <= chapters.length) setActive(n);
    },
    { rootMargin: '-40% 0px -55% 0px', threshold: [0, 0.25, 0.5, 0.75, 1] },
  );
  document.querySelectorAll<HTMLElement>('.chapter').forEach((el) => observer.observe(el));
  setActive(1);
}
